import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Crown, Zap, ArrowRight, ShieldCheck } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { Navbar } from '../../components/Navbar.jsx';
import { SubscriptionPaymentModal } from '../../componentes/SubscriptionPaymentModal.jsx';
import { api } from '@/lib/api.ts';

// --- FONDO AMBIENTAL ---
const AmbientBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none bg-[#020202]">
    <div className="absolute top-[-10%] left-[20%] w-[60vw] h-[60vw] bg-indigo-900/20 rounded-full blur-[150px] opacity-30 mix-blend-screen animate-pulse" />
    <div className="absolute bottom-[-10%] right-[20%] w-[50vw] h-[50vw] bg-purple-900/10 rounded-full blur-[150px] opacity-20 mix-blend-screen" />
  </div>
);

export function Precios() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Data fetching
  const { data: subscriptionData } = useQuery({
    queryKey: ['subscription', 'status'],
    queryFn: async () => (await api.get('/api/subscription/status')).data,
    enabled: !!localStorage.getItem('tiendaya_token'),
  });

  const { data: paymentMethodsData } = useQuery({
    queryKey: ['payment-methods', 'active'],
    queryFn: async () => (await api.get('/api/auth/super-admin/payment-methods/active/')).data,
  });

  // No necesitamos tasas aquí, solo en el modal

  // Obtener planes reales desde el backend (solo si está logueado)
  const { data: plansData } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => (await api.get('/api/plans/')).data,
    enabled: !!localStorage.getItem('tiendaya_token'),
  });

  const handleSubscribe = async (planSlug) => {
    if (!localStorage.getItem('tiendaya_token')) {
      window.location.href = '/inicio-sesion';
      return;
    }
    
    // Buscar por precio (más confiable que slug)
    const priceMap = { 'basic': 10, 'pro': 25, 'premium': 50 };
    const targetPrice = priceMap[planSlug];
    const realPlan = plansData?.find(p => parseFloat(p.price) === targetPrice);
    
    if (!realPlan) {
      console.error('Plans available:', plansData);
      console.error('Looking for price:', targetPrice);
      alert('Error: Plan no encontrado. Por favor recarga la página.');
      return;
    }
    
    setSelectedPlan(realPlan);
    setShowPaymentModal(true);
  };

  const handleCloseModal = () => {
    setShowPaymentModal(false);
    setSelectedPlan(null);
  };



  // Función para verificar si es el plan actual Y está activo
  const isCurrentPlan = (planSlug) => {
    if (!subscriptionData) return false;
    const currentPlanName = subscriptionData.plan_name?.toLowerCase() || '';
    const isActive = subscriptionData.subscription_status === 'ACTIVE';
    return currentPlanName.includes(planSlug) && isActive;
  };

  // Función para verificar si necesita renovación (cualquier plan cuando está suspendido)
  const needsRenewal = () => {
    if (!subscriptionData) return false;
    return subscriptionData.subscription_status === 'SUSPENDED';
  };

  // Función para verificar si el usuario ya usó su trial (tiene o tuvo un plan de pago)
  const hasUsedTrial = () => {
    if (!subscriptionData) return false;
    // Si el usuario tiene un plan activo que NO es trial, ya usó su trial
    // O si está suspendido pero el plan no es trial, también ya lo usó
    const currentPlanName = subscriptionData.plan_name?.toLowerCase() || '';
    const isNotTrial = !currentPlanName.includes('trial') && !currentPlanName.includes('prueba');
    return isNotTrial;
  };

  return (
    <div className="min-h-screen text-slate-200 font-sans selection:bg-indigo-500/30 flex flex-col">
      <AmbientBackground />
      <Navbar />

      {/* 
          CORRECCIÓN: 
          Cambiado 'pt-32' a 'pt-24' para subir el contenido.
      */}
      <section className="relative pt-24 pb-12 px-6 overflow-hidden">
        <div className="mx-auto max-w-4xl text-center relative z-10">
           <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 backdrop-blur-sm mb-6">
              <Crown className="h-4 w-4 text-indigo-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-300">Planes Flexibles</span>
           </div>
           
           <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-6 leading-tight">
             Elige el Poder para <br/>
             <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">Escalar tu Negocio</span>
           </h1>
           
           <p className="text-white/50 text-lg max-w-2xl mx-auto mb-8">
             Tecnología de punta al alcance de tu mano. Comienza gratis, crece sin límites.
           </p>

           {subscriptionData && (
            <div className="inline-flex items-center gap-4 p-4 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md">
               <div className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400"><CheckCircle2 className="h-5 w-5"/></div>
               <div className="text-left">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Plan Actual</p>
                  <p className="text-white font-medium text-sm">{subscriptionData.plan_name} • <span className={subscriptionData.subscription_status === 'ACTIVE' ? 'text-emerald-400' : 'text-amber-400'}>{subscriptionData.subscription_status}</span></p>
               </div>
            </div>
           )}
        </div>
      </section>

      {/* PLANES */}
      <section className="py-10 px-6">
         <div className="mx-auto max-w-7xl">
            <div className={`grid gap-8 items-stretch ${hasUsedTrial() ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
               
               {/* TRIAL GRATIS - Solo mostrar si NO ha usado el trial */}
               {!hasUsedTrial() && (
                  <div className="rounded-[2rem] border border-white/5 bg-[#0A0A0A] p-8 hover:border-white/20 transition-colors flex flex-col h-full">
                     <h3 className="text-xl font-bold text-white mb-2">Prueba Gratis</h3>
                     <div className="text-3xl font-bold text-white mb-1">
                        $0 <span className="text-sm text-slate-500 font-normal">/mes</span>
                     </div>
                     <p className="text-xs text-emerald-400 mb-6 uppercase tracking-widest">15 días sin compromiso</p>
                     
                     <ul className="space-y-4 mb-8 flex-grow">
                        {['Hasta 50 productos', '100 Pedidos de Prueba', 'Acceso Completo por 15 Días', 'Notificaciones Push + Telegram'].map(i => (
                           <li key={i} className="flex items-center gap-3 text-sm text-slate-400">
                              <CheckCircle2 className="h-4 w-4 text-white/20" /> {i}
                           </li>
                        ))}
                     </ul>

                     {isCurrentPlan('trial') ? (
                        <div className="w-full py-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-center text-sm font-bold uppercase tracking-widest text-emerald-400 cursor-not-allowed">
                           Plan Actual
                        </div>
                     ) : needsRenewal() ? (
                        <Link to="/registro/tienda" className="block w-full py-3 rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-center text-sm font-bold uppercase tracking-widest text-amber-400 transition-colors">
                           Seleccionar Plan
                        </Link>
                     ) : (
                        <Link to="/registro/tienda" className="block w-full py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-center text-sm font-bold uppercase tracking-widest text-white transition-colors">
                           Empezar Gratis
                        </Link>
                     )}
                  </div>
               )}
               
               {/* BÁSICO */}
               <div className="rounded-[2rem] border border-white/5 bg-[#0A0A0A] p-8 hover:border-white/20 transition-colors flex flex-col h-full">
                  <h3 className="text-xl font-bold text-white mb-2">Plan Básico</h3>
                  <div className="text-3xl font-bold text-white mb-6">
                     $10 <span className="text-sm text-slate-500 font-normal">/mes</span>
                  </div>
                  
                  <ul className="space-y-4 mb-8 flex-grow">
                     {['200 Productos', '500 Pedidos/mes', 'Notificaciones Push + Telegram', 'Reportes Avanzados', 'Gestionar Clientes'].map(i => (
                        <li key={i} className="flex items-center gap-3 text-sm text-slate-400">
                           <CheckCircle2 className="h-4 w-4 text-white/20" /> {i}
                        </li>
                     ))}
                  </ul>

                  {/* Siempre permitir seleccionar cualquier plan cuando está logueado */}
                  <button onClick={() => handleSubscribe('basic')} className="w-full py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-center text-sm font-bold uppercase tracking-widest text-white transition-colors">
                     {isCurrentPlan('basic') || isCurrentPlan('básico') ? 'Renovar Plan' : 'Seleccionar Plan'}
                  </button>
               </div>

               {/* PRO (Popular) */}
               <div className="relative rounded-[2rem] border border-indigo-500/50 bg-[#0F0F0F]/80 p-8 shadow-[0_0_40px_rgba(79,70,229,0.15)] flex flex-col h-full">
                  <div className="absolute -top-4 left-1/2 transform translate-x-[-50%] px-4 py-1 bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-full">Más Popular</div>
                  
                  <h3 className="text-xl font-bold text-white mb-2">Plan Pro</h3>
                  <div className="text-3xl font-bold text-white mb-6">
                     $25 <span className="text-sm text-slate-500 font-normal">/mes</span>
                  </div>
                  
                  <ul className="space-y-4 mb-8 flex-grow">
                     {['1,000 Productos', '2,000 Pedidos/mes', 'Notificaciones Push + Telegram + Email', 'Reportes Avanzados', 'Gestionar Clientes'].map(i => (
                        <li key={i} className="flex items-center gap-3 text-sm text-slate-300">
                           <CheckCircle2 className="h-4 w-4 text-indigo-400" /> {i}
                        </li>
                     ))}
                  </ul>

                  {/* Siempre permitir seleccionar cualquier plan cuando está logueado */}
                  <button onClick={() => handleSubscribe('pro')} className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 shadow-lg text-center text-sm font-bold uppercase tracking-widest text-white transition-colors">
                     {isCurrentPlan('pro') ? 'Renovar Plan' : 'Comenzar Ahora'}
                  </button>
               </div>

               {/* PREMIUM */}
               <div className="rounded-[2rem] border border-white/5 bg-[#0A0A0A] p-8 hover:border-white/20 transition-colors flex flex-col h-full">
                  <h3 className="text-xl font-bold text-white mb-2">Plan Premium</h3>
                  <div className="text-3xl font-bold text-white mb-6">
                     $50 <span className="text-sm text-slate-500 font-normal">/mes</span>
                  </div>
                  
                  <ul className="space-y-4 mb-8 flex-grow">
                     {['Productos Ilimitados', 'Pedidos Ilimitados', 'Notificaciones Push + Telegram + Email', 'Reportes Avanzados', 'Gestionar Clientes'].map(i => (
                        <li key={i} className="flex items-center gap-3 text-sm text-slate-400">
                           <CheckCircle2 className="h-4 w-4 text-white/20" /> {i}
                        </li>
                     ))}
                  </ul>

                  {/* Siempre permitir seleccionar cualquier plan cuando está logueado */}
                  <button onClick={() => handleSubscribe('premium')} className="w-full py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-center text-sm font-bold uppercase tracking-widest text-white transition-colors">
                     {isCurrentPlan('premium') ? 'Renovar Plan' : 'Seleccionar Plan'}
                  </button>
               </div>

            </div>
         </div>
      </section>

      {/* COMPARATIVA RÁPIDA */}
      <section className="py-20 px-6">
         <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-bold text-white text-center mb-12">Por qué elegir TiendasYA</h2>
            <div className="grid md:grid-cols-3 gap-8">
               <div className="p-6 rounded-2xl border border-white/5 bg-white/5 flex gap-4">
                  <div className="p-3 rounded-xl bg-indigo-500/20 text-indigo-400"><Zap className="h-6 w-6"/></div>
                  <div>
                     <h3 className="text-white font-bold text-lg">Sin Complicaciones</h3>
                     <p className="text-white/40 text-sm mt-1">Crea tu tienda en minutos. Sin código, sin configuraciones complejas, sin dolores de cabeza.</p>
                  </div>
               </div>
               <div className="p-6 rounded-2xl border border-white/5 bg-white/5 flex gap-4">
                  <div className="p-3 rounded-xl bg-emerald-500/20 text-emerald-400"><ShieldCheck className="h-6 w-6"/></div>
                  <div>
                     <h3 className="text-white font-bold text-lg">Gestión Profesional</h3>
                     <p className="text-white/40 text-sm mt-1">Panel completo con reportes avanzados, análisis de clientes y control de inventario en tiempo real.</p>
                  </div>
               </div>
               <div className="p-6 rounded-2xl border border-white/5 bg-white/5 flex gap-4">
                  <div className="p-3 rounded-xl bg-purple-500/20 text-purple-400"><CheckCircle2 className="h-6 w-6"/></div>
                  <div>
                     <h3 className="text-white font-bold text-lg">Alertas Inteligentes</h3>
                     <p className="text-white/40 text-sm mt-1">Recibe notificaciones al instante por cada pedido, pago confirmado o producto agotado.</p>
                  </div>
               </div>
            </div>
         </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 px-6 text-center">
         <h2 className="text-3xl font-bold text-white mb-6">¿Listo para despegar?</h2>
         <Link to="/registro/tienda" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black text-sm font-bold uppercase tracking-widest hover:bg-indigo-50 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.3)]">
            Comenzar Prueba Gratis <ArrowRight className="h-4 w-4" />
         </Link>
      </section>

      <SubscriptionPaymentModal
        isOpen={showPaymentModal}
        onClose={handleCloseModal}
        plan={selectedPlan}
        paymentMethods={paymentMethodsData || []}
      />
    </div>
  );
}